/**
 * Adaptador IndexedDB do outbox e do cache da viagem ativa.
 *
 * A única parte deste pacote que toca o navegador. Toda a regra vive em
 * `outbox.ts`, que é pura — então o que se testa aqui é integração, não lógica.
 *
 * Persistência: no iOS o Safari pode descartar dados de PWA sem uso
 * prolongado. `requestPersistentStorage` reduz muito essa chance quando a PWA
 * está instalada na tela de início. Não é garantia, e o servidor continua
 * sendo a verdade — mas é a diferença entre perder escritas offline e não.
 */

import { type IDBPDatabase, openDB } from 'idb';

import type { Mutation, OutboxStore } from './outbox.js';

const DB_NAME = 'nossa-viagem';
/** 2: `fotos` — a fila de envio das Memórias sobrevive a fechar o app (rodada 3, B4). */
const DB_VERSION = 2;

const STORE_OUTBOX = 'outbox';
const STORE_CACHE = 'cache';
const STORE_FOTOS = 'fotos';

/**
 * Uma foto esperando para subir. O `arquivo` é o Blob original: comprimir de
 * novo ao retomar é mais barato do que guardar as três variantes.
 */
export interface FotoPendente {
  id: string;
  tripId: string;
  dayId: string;
  activityId: string | null;
  nome: string;
  arquivo: Blob;
  lastModified: number;
  estado: 'aguardando' | 'erro';
  erro: string | null;
  createdAt: number;
}

export interface LocalDatabase {
  outbox: OutboxStore;
  /** Fila de fotos das Memórias, durável: some só quando a foto sobe ou a pessoa descarta. */
  fotos: {
    list(): Promise<FotoPendente[]>;
    put(item: FotoPendente): Promise<void>;
    remove(id: string): Promise<void>;
    clear(): Promise<void>;
  };
  /** Cache de leitura da viagem ativa, chaveado por rota/consulta. */
  cache: {
    get<T>(key: string): Promise<T | undefined>;
    set(key: string, value: unknown): Promise<void>;
    clear(): Promise<void>;
  };
  close(): void;
}

export async function openLocalDatabase(): Promise<LocalDatabase> {
  const db: IDBPDatabase = await openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE_OUTBOX)) {
        const store = database.createObjectStore(STORE_OUTBOX, { keyPath: 'mutationId' });
        // A ordem de envio é a de criação — e ela é obrigatória, porque
        // mutações posteriores dependem das anteriores.
        store.createIndex('createdAt', 'createdAt');
      }
      if (!database.objectStoreNames.contains(STORE_CACHE)) {
        database.createObjectStore(STORE_CACHE);
      }
      if (!database.objectStoreNames.contains(STORE_FOTOS)) {
        const fotos = database.createObjectStore(STORE_FOTOS, { keyPath: 'id' });
        fotos.createIndex('createdAt', 'createdAt');
      }
    },
  });

  return {
    outbox: {
      list: async () => (await db.getAllFromIndex(STORE_OUTBOX, 'createdAt')) as Mutation[],
      put: async (mutation) => {
        await db.put(STORE_OUTBOX, mutation);
      },
      remove: async (mutationId) => {
        await db.delete(STORE_OUTBOX, mutationId);
      },
    },
    fotos: {
      list: async () => (await db.getAllFromIndex(STORE_FOTOS, 'createdAt')) as FotoPendente[],
      put: async (item) => {
        await db.put(STORE_FOTOS, item);
      },
      remove: async (id) => {
        await db.delete(STORE_FOTOS, id);
      },
      clear: async () => {
        await db.clear(STORE_FOTOS);
      },
    },
    cache: {
      get: async <T>(key: string) => (await db.get(STORE_CACHE, key)) as T | undefined,
      set: async (key, value) => {
        await db.put(STORE_CACHE, value, key);
      },
      clear: async () => {
        await db.clear(STORE_CACHE);
      },
    },
    close: () => db.close(),
  };
}

/**
 * Pede armazenamento persistente.
 *
 * Devolve `false` também quando a API não existe — o chamador trata os dois
 * casos igual: seguir funcionando, mas sem contar com durabilidade.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/** Estimativa de uso e cota, para avisar antes de o navegador despejar. */
export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return { usage, quota };
  } catch {
    return null;
  }
}
